import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useWatchlist() {
  const { user } = useAuth();

  const { data: watchlist = [], isLoading } = useQuery({
    queryKey: ["watchlist", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_watchlist")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  return { watchlist, isLoading, isWatched: (ticker: string) => watchlist.some((w) => w.ticker === ticker) };
}

export function useToggleWatchlist() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticker, isWatched }: { ticker: string; isWatched: boolean }) => {
      if (!user) throw new Error("Must be signed in");
      if (isWatched) {
        const { error } = await supabase
          .from("asset_watchlist")
          .delete()
          .eq("user_id", user.id)
          .eq("ticker", ticker);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("asset_watchlist")
          .insert({ user_id: user.id, ticker });
        if (error) throw error;
      }
    },
    onSuccess: (_, { ticker, isWatched }) => {
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success(isWatched ? `${ticker} removed from watchlist` : `${ticker} added to watchlist`);
    },
    onError: () => {
      toast.error("Failed to update watchlist");
    },
  });
}
